import React, { useCallback, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import TaskEditorLivePreview from "./TaskEditorLivePreview";
import TaskEditorWorkspace from "./TaskEditorWorkspace";
import TaskEditorCodeView from "./TaskEditorCodeView";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import CustomModal from "components/common/CustomModal";
import UndoIcon from "@mui/icons-material/Undo";

interface TaskWorkspacePanelProps {
  editorState?: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
  hasUnsavedChanges: boolean;
  isCommitting: boolean;
  onCommit: () => void;
  onEditMeta: () => void;
  // hasUnsavedChanges=true 일 때만 의미. working vs V1 비교·복원에 사용.
  v1Task?: TaskEditorState;
  onDiscardWorking?: () => void | Promise<void>;
  // true 면 본문을 hydration skeleton 으로 대체 — 첫 로드/전환 시 detail 미로드 구간.
  // 트리거 판정(detailLoadedTaskIds)은 부모(TaskEditorPage)가 렌더 중 파생한다.
  isHydrating?: boolean;
  // true 면 활동지가 0개 — "활동지를 선택하거나 만드세요" 빈 안내. 로딩 중엔 false(→ skeleton).
  showNoTasks?: boolean;
}

type WorkspaceTab = "edit" | "preview" | "code";

const TAB_LABELS: Record<WorkspaceTab, string> = {
  edit: "편집",
  preview: "미리보기",
  code: "코드",
};

const KEY_BADGE_COLOR = "#F59E0B";

// 활동지 전환 직후 잠깐 보이는 헤더/본문/푸터 블록 자리를 채울 hydration skeleton 카드.
// MyProgressV2(진도표) 로딩 카드와 동일한 스타일 — 둥근 16px, Surface.Forward, 테두리/그림자 없음.
const hydrationSkeletonCardSx = {
  borderRadius: "16px",
  px: "20px",
  py: "18px",
  backgroundColor: theme.vars.palette.Surface.Forward,
} as const;

// 첫 로드/전환 시 detail 미도착 구간 — 실제로 잠깐 보이던 헤더/본문/푸터 블록 자리를 같은
// 레이아웃의 skeleton 으로 대체(MyProgressV2 진도표 로딩 스타일). 초기 로드와 본문 로딩 두 곳에서 재사용.
function HydrationSkeleton() {
  return (
    <Box
      data-testid="task-editor-hydration-skeleton"
      sx={{ px: "12px", py: "16px" }}
    >
      <Stack spacing={1} sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}>
        {/* 헤더 블록 */}
        <Box sx={hydrationSkeletonCardSx}>
          <Skeleton
            variant="rectangular"
            height={28}
            sx={{ borderRadius: "8px", width: "45%" }}
          />
        </Box>
        {/* 본문 블록 */}
        <Box sx={hydrationSkeletonCardSx}>
          <Stack spacing={1}>
            <Skeleton
              variant="rectangular"
              height={28}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rectangular"
              height={28}
              sx={{ borderRadius: "8px", width: "80%" }}
            />
          </Stack>
        </Box>
        {/* 푸터(마치기) 블록 — 흐림 */}
        <Box sx={{ ...hydrationSkeletonCardSx, opacity: 0.55 }}>
          <Skeleton
            variant="rectangular"
            height={28}
            sx={{ borderRadius: "8px", width: "35%" }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default function TaskWorkspacePanel({
  editorState,
  onChangeEditorState,
  hasUnsavedChanges,
  isCommitting,
  onCommit,
  onEditMeta,
  v1Task,
  onDiscardWorking,
  isHydrating,
  showNoTasks,
}: TaskWorkspacePanelProps) {
  const [tab, setTab] = useState<WorkspaceTab>("edit");
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // 미리보기에서 블록을 눌러 편집 탭으로 넘어갈 때, 포커스할 블록 index(0-based).
  // 편집 워크스페이스가 마운트되며 이 값을 소비하고 onFocusConsumed 로 비운다.
  const [pendingFocusBlockIndex, setPendingFocusBlockIndex] = useState<
    number | null
  >(null);

  const handleEditBlock = useCallback((blockIndex: number) => {
    setTab("edit");
    setPendingFocusBlockIndex(blockIndex);
  }, []);

  const handleFocusConsumed = useCallback(() => {
    setPendingFocusBlockIndex(null);
  }, []);

  // 활동지가 정말 0개일 때만 빈 안내. (로딩 중엔 showNoTasks=false → 아래 skeleton)
  if (showNoTasks) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: theme.vars.palette.Content.Weak,
          p: "32px",
        }}
      >
        <Typography level="body-md" sx={{ mb: "4px" }}>
          활동지를 선택하거나 만드세요
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          좌측 목록에서 활동지를 선택하거나 [+ 새 활동지] 버튼을 눌러
          시작하세요.
        </Typography>
      </Box>
    );
  }
  // 초기 로드(메타 도착 전, editorState 없음) — 헤더 없이 본문 skeleton 만. 빈 안내가 새지 않는다.
  // (editorState 가 있으면 헤더/제목은 유지하고 아래 본문에서만 skeleton 으로 전환한다.)
  if (!editorState) {
    return <HydrationSkeleton />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, flexWrap: "nowrap" }}
          >
            <Typography
              level="title-sm"
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {editorState.title || "(제목 없음)"}
            </Typography>
            {editorState.is_key_activity && (
              <Typography
                level="body-xs"
                sx={{
                  color: KEY_BADGE_COLOR,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
                title="Key 활동지"
              >
                ★
              </Typography>
            )}
            <Typography
              level="body-xs"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {editorState.task_id || "(ID 없음)"} ·{" "}
            </Typography>
            {editorState.set_number !== undefined && (
              <Typography
                level="body-xs"
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "11px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                세트 {editorState.set_number}
              </Typography>
            )}
            <Box
              className="meta-edit-btn"
              onClick={onEditMeta}
              sx={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                color: theme.vars.palette.Content.Weak,
                cursor: "pointer",
                px: "2px",
                "&:hover": {
                  color: theme.vars.palette.Content.Strong,
                },
              }}
              title="메타데이터 편집"
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                more_vert
              </span>
            </Box>
          </Stack>
        </Box>

        {hasUnsavedChanges && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setDiscardConfirmOpen(true)}
              disabled={!v1Task || isCommitting || !onDiscardWorking}
              aria-label="원복"
              title="원복"
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                mr: "8px",
                color: theme.vars.palette.Content.Weak,
                "&:hover": { color: theme.vars.palette.Content.Medium },
                "&:disabled": {
                  color: theme.vars.palette.Content.Weak,
                  opacity: 0.4,
                  cursor: "default",
                },
              }}
            >
              <UndoIcon sx={{ fontSize: "18px" }} />
            </Box>
          </>
        )}
        <Button
          size="sm"
          variant="Solid"
          color="primary"
          onClick={onCommit}
          disabled={!hasUnsavedChanges || isCommitting}
          loading={isCommitting}
          sx={{ fontSize: "12px", minHeight: "28px" }}
        >
          {"커밋"}
        </Button>
      </Box>

      <CustomModal
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        title="변경 내용을 원복할까요?"
        subtitle="커밋되지 않은 변경 내용이 모두 사라지고 마지막 커밋 상태로 되돌아갑니다."
        actions={[
          {
            label: "원복하기",
            variant: "Solid",
            color: "danger",
            onClick: () => {
              setDiscardConfirmOpen(false);
              void onDiscardWorking?.();
            },
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: () => setDiscardConfirmOpen(false),
          },
        ]}
      />

      <Box
        sx={{
          display: "flex",
          gap: "4px",
          px: "12px",
          pt: "6px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {(["edit", "preview", "code"] as const).map((tabId) => {
          const isActive = tab === tabId;
          return (
            <Box
              key={tabId}
              onClick={() => setTab(tabId)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "12px",
                userSelect: "none",
              }}
            >
              {TAB_LABELS[tabId]}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          ...taskEditorScrollbarSx,
        }}
      >
        {isHydrating ? (
          <HydrationSkeleton />
        ) : (
          <>
            {tab === "edit" && (
              <TaskEditorWorkspace
                editorState={editorState}
                onChangeEditorState={onChangeEditorState}
                focusBlockIndex={pendingFocusBlockIndex}
                onFocusConsumed={handleFocusConsumed}
              />
            )}
            {tab === "preview" && (
              <Box sx={{ px: "12px", py: "8px" }}>
                <TaskEditorLivePreview
                  task={editorState}
                  onEditBlock={handleEditBlock}
                />
              </Box>
            )}
            {tab === "code" && (
              <Box
                sx={{
                  px: "9%",
                  pt: "8px",
                  pb: "12px",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <TaskEditorCodeView task={editorState} v1Task={v1Task} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
